import { App } from '@capacitor/app';
import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

/**
 * The game talks to Telegram.WebApp everywhere (back button, haptics, links).
 * Outside Telegram those calls silently do nothing, so in the App Store /
 * Play build their methods are backed by native equivalents instead — the
 * call sites stay exactly as they are.
 */

// A swipe that starts this close to the left edge and travels this far
// right is iOS's "back" gesture.
const EDGE_PX = 24;
const SWIPE_PX = 70;

const backHandlers = new Set<() => void>();
let backVisible = false;

const goBack = (): boolean => {
  if (!backVisible || backHandlers.size === 0) return false;
  backHandlers.forEach((handler) => handler());

  return true;
};

const installBackButton = (webApp: typeof Telegram.WebApp): void => {
  const button = webApp.BackButton;

  button.onClick = (callback: () => void): typeof button => {
    backHandlers.add(callback);

    return button;
  };
  button.offClick = (callback: () => void): typeof button => {
    backHandlers.delete(callback);

    return button;
  };
  button.show = (): typeof button => {
    backVisible = true;

    return button;
  };
  button.hide = (): typeof button => {
    backVisible = false;

    return button;
  };

  // Android: the hardware / gesture back key
  void App.addListener('backButton', () => {
    if (!goBack()) void App.minimizeApp();
  });

  // iOS: swipe from the left edge
  let startX: number | null = null;
  let startY = 0;

  window.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.touches[0];
      startX = touch.clientX <= EDGE_PX ? touch.clientX : null;
      startY = touch.clientY;
    },
    { passive: true },
  );
  window.addEventListener(
    'touchend',
    (event) => {
      if (startX === null) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);
      startX = null;

      if (dx > SWIPE_PX && dy < dx / 2) goBack();
    },
    { passive: true },
  );
};

const impactStyles: Record<string, ImpactStyle> = {
  light: ImpactStyle.Light,
  soft: ImpactStyle.Light,
  medium: ImpactStyle.Medium,
  heavy: ImpactStyle.Heavy,
  rigid: ImpactStyle.Heavy,
};

const notificationTypes: Record<string, NotificationType> = {
  success: NotificationType.Success,
  warning: NotificationType.Warning,
  error: NotificationType.Error,
};

const installHaptics = (webApp: typeof Telegram.WebApp): void => {
  const haptics = webApp.HapticFeedback;

  haptics.impactOccurred = (style): typeof haptics => {
    void Haptics.impact({ style: impactStyles[style] ?? ImpactStyle.Light });

    return haptics;
  };
  haptics.notificationOccurred = (type): typeof haptics => {
    void Haptics.notification({
      type: notificationTypes[type] ?? NotificationType.Success,
    });

    return haptics;
  };
  haptics.selectionChanged = (): typeof haptics => {
    void Haptics.selectionChanged();

    return haptics;
  };
};

const installLinks = (webApp: typeof Telegram.WebApp): void => {
  // t.me links: the Telegram app if installed (universal link), else Safari
  webApp.openTelegramLink = (url: string): void => {
    void AppLauncher.openUrl({ url }).catch(() => Browser.open({ url }));
  };
  webApp.openLink = (url: string): void => {
    void Browser.open({ url });
  };
  // There is no "close the mini app" in a native app
  webApp.close = (): void => undefined;
};

const installNativeTelegram = (): void => {
  if (!Capacitor.isNativePlatform() || typeof Telegram === 'undefined') {
    return;
  }

  const webApp = Telegram.WebApp;
  installBackButton(webApp);
  installHaptics(webApp);
  installLinks(webApp);
};

export { installNativeTelegram };
